// built-from: /platform-architecture-catalogue v1 /process-platform-overlay v1 role:implementation
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRefreshOnReturn } from './useRefreshOnReturn';

export type ArchitectureReviewState = 'proposed' | 'reviewed' | 'disputed';
export type ArchitectureSource = {
  type: string;
  contextItemId?: string;
  locator?: string;
  [key: string]: unknown;
};

export interface PlatformUse {
  state: 'current' | 'target' | 'transitional';
  status: ArchitectureReviewState;
  configuration: Record<string, unknown>;
  [key: string]: unknown;
}

export interface ArchitectureProposal {
  id: string;
  changes: Record<string, unknown>;
  source: ArchitectureSource;
  reason: string;
}

export interface PlatformRecord {
  id: string;
  kind: 'platform-capability' | 'architecture-use' | 'application' | 'workload-component' | 'data-asset' | 'deployment-resource' | 'template-reference' | 'architecture-boundary';
  name: string;
  purpose: string;
  source: ArchitectureSource;
  reviewState: ArchitectureReviewState;
  uses: PlatformUse[];
  availability?: 'proposed' | 'available' | 'retired';
  capabilityRecordId?: string;
  state?: 'current' | 'target' | 'transitional';
  environment?: string;
  configuration?: Record<string, unknown>;
  retiredAt?: string | null;
  proposedChanges?: ArchitectureProposal[];
  [key: string]: unknown;
}

export interface PlatformRequirement {
  id: string;
  text: string;
  source: ArchitectureSource;
  scope: string;
  origin: 'stated' | 'inferred';
  targets: Array<Record<string, unknown>>;
  reviewState: ArchitectureReviewState;
  proposedChanges?: ArchitectureProposal[];
  [key: string]: unknown;
}

export interface PlatformDecision {
  id: string;
  title?: string;
  text?: string;
  choice: string;
  reasoning: string;
  alternatives: string[];
  requirementIds: string[];
  source: ArchitectureSource;
  reviewState: ArchitectureReviewState;
  proposedChanges?: ArchitectureProposal[];
  [key: string]: unknown;
}

export interface PlatformRelationship {
  id: string;
  fromRecordId: string;
  toRecordId: string;
  kind: string;
  source: ArchitectureSource;
  reviewState: ArchitectureReviewState;
  proposedChanges?: ArchitectureProposal[];
  [key: string]: unknown;
}

export interface PlatformArchitectureModel {
  version: 1 | 2 | 3;
  designRevision?: number;
  proposals?: PlatformDesignProposal[];
  records: PlatformRecord[];
  requirements: PlatformRequirement[];
  decisions: PlatformDecision[];
  relationships: PlatformRelationship[];
}

export interface PlatformDesignProposalApplication {
  acceptedDesignRevision: number;
  status: 'complete' | 'partial' | 'needs-review';
  overlayResults: Array<{ processId: string; state: 'pending' | 'applied' | 'conflict' | 'failed'; revision?: string; error?: string }>;
}

export interface PlatformDesignProposal {
  id: string;
  contentRevision: number;
  status: 'pending' | 'accepted' | 'dismissed';
  explanation: string;
  scopeRecordIds: string[];
  operations: Array<{ type: string; value: Record<string, unknown> }>;
  designTaskContractVersion?: 1;
  plannedOverlays?: Array<{ processId: string; links: ProcessPlatformLink[]; stepAnnotations?: ProcessStepAnnotation[] }>;
  application?: PlatformDesignProposalApplication;
}

export interface ProcessPlatformLink {
  id: string;
  stepId: string;
  recordId: string;
  interaction: string;
  relationshipId?: string;
  order?: number;
  relationshipFromRecordId?: string;
  relationshipToRecordId?: string;
  source: ArchitectureSource;
  reviewState: ArchitectureReviewState;
  overlayProcessId?: string;
  [key: string]: unknown;
}

export interface ProcessPlatformOverlay {
  version: 1 | 2;
  processId: string;
  links: ProcessPlatformLink[];
  stepAnnotations?: ProcessStepAnnotation[];
  reviewedProcessRevision?: string | null;
}

export interface ProcessStepAnnotation {
  id: string;
  stepId: string;
  narrative?: string;
  outcome?: string;
  source: ArchitectureSource;
  reviewState: ArchitectureReviewState;
}

export interface PlatformProcessStep {
  stepId: string;
  processId: string;
  label: string;
  order: number;
  parentStepId: string | null;
  trigger?: { kind: string; text: string };
  alternative?: { groupId: string; label: string };
  links: ProcessPlatformLink[];
  interactions?: Array<ProcessPlatformLink & { linkId: string; relationshipId: string; order: number; fromRecordId: string; toRecordId: string; relationship?: PlatformRelationship }>;
  stepAnnotations?: ProcessStepAnnotation[];
}

export interface UnresolvedPlatformMapping {
  linkId?: string;
  stepId: string;
  recordId?: string;
  reason: string;
  needsReview: true;
}

export interface PlatformArchitectureProjection {
  processId: string;
  processRevision: string;
  overlayRevision: string;
  sourceOverlayRevisions: Record<string, string>;
  processRevisions: Record<string, string>;
  steps: PlatformProcessStep[];
  unresolvedMappings: UnresolvedPlatformMapping[];
}

export interface PlatformProposalPreview {
  previewId: string;
  proposalId: string;
  contentRevision: number;
  model: PlatformArchitectureModel;
  projections: Record<string, PlatformArchitectureProjection>;
  changedRecordIds: string[];
  changedRelationshipIds: string[];
  issues: Array<{ code: string; message?: string; processId?: string }>;
  designDetail?: PlatformArchitectureDesignDetail;
}

export interface PlatformArchitectureDesignDetail {
  level: 'concept' | 'infrastructure-defined';
  summary: string;
  gaps: Array<{ code: string; recordId?: string; message: string; nextAction?: string }>;
}

export interface PlatformOverlayReviewVersions {
  modelRevision?: string | null;
  processRevisions?: Record<string, string>;
}

export type PlatformArchitectureChange =
  | { type: 'upsert-record'; value: PlatformRecord }
  | { type: 'upsert-requirement'; value: PlatformRequirement }
  | { type: 'upsert-decision'; value: PlatformDecision }
  | { type: 'upsert-relationship'; value: PlatformRelationship }
  | { type: 'remove-requirement' | 'remove-decision' | 'remove-relationship'; id: string }
  | { type: 'propose-design'; proposal: { id: string; explanation: string; scopeRecordIds: string[]; operations: Array<{ type: 'upsert-record' | 'upsert-requirement' | 'upsert-decision' | 'upsert-relationship'; value: Record<string, unknown> }> } }
  | { type: 'revise-design-proposal'; proposalId: string; contentRevision: number; explanation: string; scopeRecordIds: string[]; operations: Array<{ type: string; value: Record<string, unknown> }> }
  | { type: 'resolve-design-proposal'; proposalId: string; contentRevision: number; decision: 'accept' | 'dismiss' }
  | {
    type: 'apply-proposal' | 'dismiss-proposal';
    itemKind: 'record' | 'requirement' | 'decision' | 'relationship';
    itemId: string;
    proposalId: string;
  };

interface ApiFailure { error?: string; code?: string }

function apiFailure(status: number, body: ApiFailure): string {
  if (status === 409 || body.code === 'PLATFORM_ARCHITECTURE_CONFLICT') {
    return 'This architecture changed since you opened it. Your edit is still here; review the latest version before trying again.';
  }
  if (status === 400 || status === 422) {
    return body.error || 'That architecture change needs a look. Your edit is still here.';
  }
  return body.error || "We couldn't save that change. Your edit is still here; check the connection and try again.";
}

export function usePlatformArchitecture(engagementId: string | null, activityId: string | null) {
  const [model, setModel] = useState<PlatformArchitectureModel>();
  const [modelRevision, setModelRevision] = useState<string | null>();
  const [modelEngagementId, setModelEngagementId] = useState<string | null>(null);
  const [overlay, setOverlay] = useState<ProcessPlatformOverlay>();
  const [overlayRevision, setOverlayRevision] = useState<string | null>();
  const [projection, setProjection] = useState<PlatformArchitectureProjection>();
  const [proposalPreview, setProposalPreview] = useState<PlatformProposalPreview>();
  const [designDetail, setDesignDetail] = useState<PlatformArchitectureDesignDetail>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const selectedProcess = useRef<string | null>(null);
  const requestSequence = useRef(0);
  const processRequestSequence = useRef(0);
  const mutationSequence = useRef(0);
  const savingRef = useRef(false);

  const base = engagementId
    ? `/api/engagements/${encodeURIComponent(engagementId)}/platform-architecture`
    : '';
  const scoped = base && activityId ? `${base}?activityId=${encodeURIComponent(activityId)}` : '';
  const activeBase = useRef(base);
  activeBase.current = base;

  const readJson = useCallback(async <T,>(url: string): Promise<T> => {
    const response = await fetch(url);
    const body = await response.json().catch(() => ({})) as T & ApiFailure;
    if (!response.ok) throw new Error(apiFailure(response.status, body));
    return body;
  }, []);

  const loadModel = useCallback(async () => {
    if (!scoped) { setLoading(false); return; }
    const request = ++requestSequence.current;
    setLoading(true);
    setError(null);
    try {
      const body = await readJson<{ model: PlatformArchitectureModel; revision: string | null; proposalPreview?: PlatformProposalPreview; designDetail?: PlatformArchitectureDesignDetail }>(scoped);
      if (activeBase.current !== base || request !== requestSequence.current) return;
      setModel(body.model);
      setModelRevision(body.revision);
      setModelEngagementId(engagementId);
      setProposalPreview(body.proposalPreview);
      setDesignDetail(body.designDetail);
    } catch (cause) {
      if (activeBase.current === base && request === requestSequence.current) setError(cause instanceof Error ? cause.message : 'The architecture could not be loaded. Try again.');
    } finally {
      if (activeBase.current === base && request === requestSequence.current) setLoading(false);
    }
  }, [base, engagementId, readJson, scoped]);

  useEffect(() => {
    requestSequence.current += 1;
    processRequestSequence.current += 1;
    mutationSequence.current += 1;
    savingRef.current = false;
    setSaving(false);
    setModel(undefined);
    setModelRevision(undefined);
    setModelEngagementId(null);
    setOverlay(undefined);
    setOverlayRevision(undefined);
    setProjection(undefined);
    setProposalPreview(undefined);
    setDesignDetail(undefined);
    selectedProcess.current = null;
    void loadModel();
  }, [loadModel]);

  const loadProcess = useCallback(async (processId: string, preserveCurrent = false) => {
    if (!base || !processId) return;
    const request = ++processRequestSequence.current;
    selectedProcess.current = processId;
    if (!preserveCurrent) {
      setOverlay(undefined);
      setProjection(undefined);
    }
    setError(null);
    try {
      const path = `${base}/processes/${encodeURIComponent(processId)}/overlay`;
      const [overlayBody, projectionBody] = await Promise.all([
        readJson<{ overlay: ProcessPlatformOverlay; revision: string }>(`${path}?activityId=${encodeURIComponent(activityId || '')}`),
        readJson<PlatformArchitectureProjection>(`${path}/projection?activityId=${encodeURIComponent(activityId || '')}`),
      ]);
      if (activeBase.current !== base || selectedProcess.current !== processId || request !== processRequestSequence.current) return;
      setOverlay(overlayBody.overlay);
      setOverlayRevision(overlayBody.revision);
      setProjection(projectionBody);
    } catch (cause) {
      if (activeBase.current === base && selectedProcess.current === processId && request === processRequestSequence.current) {
        setError(cause instanceof Error ? cause.message : 'The process links could not be loaded. Try again.');
      }
    }
  }, [activityId, base, readJson]);

  useRefreshOnReturn(Boolean(base), async () => {
    if (activeBase.current !== base || savingRef.current) return;
    await loadModel();
    const processId = selectedProcess.current;
    if (processId && activeBase.current === base && !savingRef.current) await loadProcess(processId, true);
  });

  const readOverlay = useCallback(async (processId: string) => {
    if (!base || !processId) return null;
    try {
      return await readJson<{ overlay: ProcessPlatformOverlay; revision: string | null }>(
        `${base}/processes/${encodeURIComponent(processId)}/overlay?activityId=${encodeURIComponent(activityId || '')}`,
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The process links could not be loaded. Try again.');
      return null;
    }
  }, [activityId, base, readJson]);

  const invoke = useCallback(async <T,>(capabilityId: string, input: Record<string, unknown>): Promise<T | null> => {
    if (!base) return null;
    const expectedBase = base;
    const mutation = ++mutationSequence.current;
    requestSequence.current += 1;
    processRequestSequence.current += 1;
    setSaving(true);
    savingRef.current = true;
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch(`${base}/capabilities/${capabilityId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activityId, ...input }),
      });
      const body = await response.json().catch(() => ({})) as T & ApiFailure;
      if (activeBase.current !== expectedBase || mutation !== mutationSequence.current) return null;
      if (!response.ok) {
        setError(apiFailure(response.status, body));
        return null;
      }
      return body;
    } catch {
      if (activeBase.current === expectedBase && mutation === mutationSequence.current) setError("We couldn't save that change. Your edit is still here; check the connection and try again.");
      return null;
    } finally {
      if (activeBase.current === expectedBase && mutation === mutationSequence.current) {
        setLoading(false);
        setSaving(false);
        savingRef.current = false;
      }
    }
  }, [activityId, base]);

  const applyChange = useCallback(async (
    change: PlatformArchitectureChange,
    ...openedRevision: [expectedRevision?: string | null]
  ): Promise<boolean> => {
    const expectedRevision = openedRevision.length ? openedRevision[0] : modelRevision;
    const proposalChange = ['propose-design', 'revise-design-proposal', 'resolve-design-proposal'].includes(change.type);
    const result = await invoke<{ model: PlatformArchitectureModel; revision: string | null; proposalPreview?: PlatformProposalPreview; designDetail?: PlatformArchitectureDesignDetail }>(
      'platform-architecture.apply_change',
      {
        contractVersion: model?.version === 3 || proposalChange ? 3 : 2,
        ...(change.type === 'resolve-design-proposal' && model?.proposals?.find(item => item.id === change.proposalId)?.designTaskContractVersion === 1
          ? { designTaskContractVersion: 1 }
          : {}),
        expectedRevision,
        change,
      },
    );
    if (!result) return false;
    setLoading(false);
    setModel(result.model);
    setModelRevision(result.revision);
    setProposalPreview(result.proposalPreview);
    setDesignDetail(result.designDetail);
    const resolved = change.type === 'resolve-design-proposal'
      ? result.model.proposals?.find(item => item.id === change.proposalId)
      : undefined;
    setSuccess(resolved?.application && resolved.application.status !== 'complete'
      ? 'Architecture accepted. Some process mappings still need attention.'
      : 'Architecture saved.');
    if (selectedProcess.current) await loadProcess(selectedProcess.current, true);
    return true;
  }, [invoke, loadProcess, model?.proposals, model?.version, modelRevision]);

  const saveDesign = useCallback(async (previewId: string, openedRevision: string | null | undefined = modelRevision): Promise<boolean> => {
    const result = await invoke<{ model: PlatformArchitectureModel; revision: string | null; proposalPreview?: PlatformProposalPreview; designDetail?: PlatformArchitectureDesignDetail }>(
      'platform-architecture.save_design',
      { expectedRevision: openedRevision, previewId },
    );
    if (!result) return false;
    setLoading(false);
    setModel(result.model);
    setModelRevision(result.revision);
    setProposalPreview(result.proposalPreview);
    setDesignDetail(result.designDetail);
    const accepted = [...(result.model.proposals || [])].reverse().find(item => item.status === 'accepted');
    setSuccess(accepted?.application && accepted.application.status !== 'complete'
      ? 'Architecture saved. Some process mappings still need attention.'
      : 'Architecture saved.');
    if (selectedProcess.current) await loadProcess(selectedProcess.current, true);
    return true;
  }, [invoke, loadProcess, modelRevision]);

  const saveOverlay = useCallback(async (
    processId: string,
    links: ProcessPlatformLink[],
    ownerRevision: string | null | undefined = overlayRevision,
    reviewedVersions?: PlatformOverlayReviewVersions,
    stepAnnotations?: ProcessStepAnnotation[],
  ): Promise<boolean> => {
    const reviewedModelRevision = reviewedVersions ? reviewedVersions.modelRevision : modelRevision;
    const reviewedProcessRevisions = reviewedVersions?.processRevisions ?? projection?.processRevisions;
    const result = await invoke<{ overlay: ProcessPlatformOverlay; revision: string | null }>(
      'process-platform-overlay.save',
      {
        contractVersion: 2,
        expectedRevision: ownerRevision,
        modelRevision: reviewedModelRevision,
        reviewedProcessRevision: reviewedProcessRevisions?.[processId],
        reviewedProcessRevisions,
        processId,
        links,
        ...(stepAnnotations !== undefined ? { stepAnnotations } : {}),
      },
    );
    if (!result) return false;
    setOverlay(result.overlay);
    setOverlayRevision(result.revision);
    setSuccess('Process links saved.');
    await Promise.all([loadModel(), loadProcess(selectedProcess.current || processId)]);
    return true;
  }, [invoke, loadModel, loadProcess, modelRevision, overlayRevision, projection]);

  const retireRecord = useCallback(async (recordId: string): Promise<boolean> => {
    const result = await invoke<{ model: PlatformArchitectureModel; revision: string | null }>(
      'platform-architecture.retire_record',
      { contractVersion: model?.version === 3 ? 3 : 2, expectedRevision: modelRevision, recordId },
    );
    if (!result) return false;
    setModel(result.model);
    setModelRevision(result.revision);
    setSuccess('Platform detail retired. Linked process steps now need review.');
    if (selectedProcess.current) await loadProcess(selectedProcess.current, true);
    return true;
  }, [invoke, loadProcess, model?.version, modelRevision]);

  const captureEvidence = useCallback(async (activityId: string, processId: string): Promise<boolean> => {
    const result = await invoke<Record<string, unknown>>('platform-architecture.capture_evidence', {
      activityId,
      modelRevision,
      processId,
      overlayRevision,
      reviewedProcessRevision: projection?.processId === processId
        ? projection.processRevision
        : undefined,
      sourceOverlayRevisions: projection?.processId === processId
        ? projection.sourceOverlayRevisions
        : undefined,
    });
    if (!result) return false;
    setSuccess('Reviewed architecture captured as activity evidence.');
    return true;
  }, [invoke, modelRevision, overlayRevision, projection]);

  const clearFeedback = useCallback(() => { setError(null); setSuccess(null); }, []);

  return {
    model: modelEngagementId === engagementId ? model : undefined,
    modelRevision: modelEngagementId === engagementId ? modelRevision : undefined,
    overlay: modelEngagementId === engagementId ? overlay : undefined,
    overlayRevision: modelEngagementId === engagementId ? overlayRevision : undefined,
    projection: modelEngagementId === engagementId ? projection : undefined,
    proposalPreview: modelEngagementId === engagementId ? proposalPreview : undefined,
    designDetail: modelEngagementId === engagementId ? designDetail : undefined,
    loading: loading || modelEngagementId !== engagementId, saving, error, success,
    loadModel, loadProcess, readOverlay, applyChange, saveDesign, saveOverlay, retireRecord, captureEvidence, clearFeedback,
  };
}
